import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoArrowLeft } from "react-icons/go";
import { FaChevronDown } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { fetchTransactionsHistory } from "../../api/apiServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

// Logos
import mtnLogo from "../../assets/images/mtn.png";
import gloLogo from "../../assets/images/glo.png";
import airtelLogo from "../../assets/images/airtel1.png";
import ninemobileLogo from "../../assets/images/9mobile.png";
import walletLogo from "../../assets/images/wallet.png";
import { AlertTriangle } from "lucide-react";

const networkLogos = {
  MTN: mtnLogo,
  GLO: gloLogo,
  AIRTEL: airtelLogo,
  "9MOBILE": ninemobileLogo,
  Deposit: walletLogo,
  PAYSTACK_FUNDING: walletLogo,
};

const statuses = ["All Status", "Processing", "Successful", "Failed", "Cancelled", "Refunded"];
const categories = ["All Categories", "Airtime", "Data", "Deposit", "Card Deposit"];

export default function TransactionHistory() {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Fetch transactions
  const fetchData = async () => {
    setLoading(true);
    try {
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();

      const data = await fetchTransactionsHistory({
        status: selectedStatus === "All Status" ? undefined : selectedStatus.toLowerCase(),
        category: selectedCategory === "All Categories" ? undefined : selectedCategory.toLowerCase(),
        month,
        year,
        limit: 50,
      });

      // Handle your actual API structure
      const groups = data?.groups || [];
      setTransactions(groups);
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedStatus, selectedCategory, selectedDate]);

  const handleDownload = () => {
    alert("CSV download will be implemented soon");
  };

  const getAmountDisplay = (item) => {
    const isCredit = item.type === "CREDIT" || item.amount > 0;
    const amount = Math.abs(item.amount || 0);
    return {
      display: isCredit ? `+${amount.toLocaleString()}` : `-${amount.toLocaleString()}`,
      color: isCredit ? "text-green-600" : "text-red-600"
    };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Header */}
        <div className="bg-white sticky top-0 z-20 border-b px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="text-2xl text-gray-700">←</button>
                    <h1 className="text-xl font-semibold">Transaction History</h1>
                  </div>
        
                  <div className="flex items-center gap-3">
                    {/* Report Transaction button */}
                    <button
                      onClick={() => navigate("/report-transaction")}
                      className="flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-xl active:scale-95 transition-all"
                    >
                      <AlertTriangle size={13} />
                      Report
                    </button>
        
                    {/* <button onClick={handleDownload} className="text-[#3866A3] font-medium">
                      Download
                    </button> */}
                  </div>
                </div>

        <div className="px-4 pt-4">
          {/* All Status Filter */}
          <div className="relative mb-6">
            <button
              onClick={() => setShowStatusFilter(!showStatusFilter)}
              className="w-full bg-white border border-gray-200 rounded-2xl px-5 py-3 flex items-center justify-between text-left font-medium"
            >
              {selectedStatus}
              <FaChevronDown className="text-gray-500" />
            </button>

            <AnimatePresence>
              {showStatusFilter && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute w-full bg-white mt-2 rounded-2xl shadow-lg z-30 overflow-hidden border border-gray-100"
                >
                  {statuses.map((status) => (
                    <div
                      key={status}
                      onClick={() => {
                        setSelectedStatus(status);
                        setShowStatusFilter(false);
                      }}
                      className={`px-5 py-3.5 hover:bg-gray-50 cursor-pointer ${
                        selectedStatus === status ? "bg-blue-50 text-[#3866A3] font-medium" : ""
                      }`}
                    >
                      {status}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setShowFilterPanel(true)}
            className="w-full bg-white border border-gray-200 rounded-2xl py-3 text-gray-700 font-medium mb-6"
          >
            Filter
          </button>

          {/* Transactions List */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading transactions...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No transactions found</div>
          ) : (
            transactions.map((group, idx) => (
              <div key={idx} className="mb-8">
                <h2 className="font-semibold text-lg mb-4 px-1">{group.monthLabel || "Recent"}</h2>

                <div className="space-y-3">
                  {group.transactions?.map((item) => {
                    const amountInfo = getAmountDisplay(item);
                    return (
                      <div
                        key={item.id}
                        onClick={() => navigate(`/transactions/${item.id}`, {
                          state: { 
                            transaction: item
                          }
                        })}
                        className="bg-white rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all active:scale-[0.985]"
                      >
                        <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <img
                            src={networkLogos[item.network] || networkLogos["Deposit"] || walletLogo}
                            alt={item.network || "Transaction"}
                            className="w-7 h-7 object-contain"
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{item.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatDate(item.createdAt)}
                          </p>
                        </div>

                        <div className={`font-semibold text-right ${amountInfo.color}`}>
                          {amountInfo.display}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <MobileBottomNav />

      {/* Filter Panel */}
      <AnimatePresence>
        {showFilterPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50"
            onClick={() => setShowFilterPanel(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl p-6 overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-semibold text-lg mb-6">Filter</h3>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">Status</p>
                <div className="flex flex-wrap gap-2">
                  {statuses.slice(1).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedStatus(s)}
                      className={`px-4 py-2 rounded-full text-sm ${
                        selectedStatus === s ? "bg-[#3866A3] text-white" : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <p className="text-sm text-gray-600 mb-3">Category</p>
                <div className="flex flex-wrap gap-2">
                  {categories.slice(1).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2 rounded-full text-sm ${
                        selectedCategory === cat ? "bg-[#3866A3] text-white" : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-600 mb-3">Month & Year</p>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date || new Date())}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-center"
                />
              </div>

              <button
                onClick={() => {
                  setShowFilterPanel(false);
                  fetchData();
                }}
                className="mt-12 w-full bg-[#3866A3] text-white py-4 rounded-2xl font-medium"
              >
                Apply Filters
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}